/**
 * TimeSeriesChart Component
 * 
 * Cumulative line chart showing user and container growth over time.
 * Supports linear and logarithmic scales, and toggling line visibility.
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { typography, surfaces, borders, textColors } from '../../design-system/tokens';
import { formatDate, formatAxisTick } from '../utils/chartHelpers';

const TimeSeriesChart = ({
  data = [],
  scale = 'linear',
  showUsers = true,
  showContainers = true,
  loading = false,
  error = null,
}) => {
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-3 shadow-lg`}>
          <p className={`${typography.label} ${textColors.primary} mb-2`}>
            {formatDate(label, 'day')}
          </p>
          {payload.map((entry, index) => (
            <p key={index} className={`${typography.bodySmall}`} style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          User & Container Growth Over Time
        </h3>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          User & Container Growth Over Time
        </h3>
        <div className="flex flex-col items-center justify-center h-96 gap-3">
          <svg className="w-12 h-12 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className={`${typography.body} ${textColors.secondary}`}>
            Failed to load chart data
          </p>
          <p className={`${typography.bodySmall} ${textColors.muted}`}>
            {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          User & Container Growth Over Time
        </h3>
        <div className="flex flex-col items-center justify-center h-96 gap-3">
          <svg className={`w-12 h-12 ${textColors.muted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className={`${typography.body} ${textColors.secondary}`}>
            No data available for the selected date range
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
      <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
        User & Container Growth Over Time
      </h3>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="currentColor" 
            className={`${textColors.muted} opacity-30`}
          />
          
          <XAxis
            dataKey="date"
            tickFormatter={(value) => formatDate(value, 'day')}
            className={`${textColors.tertiary} opacity-80`}
            stroke="currentColor"
          />
          
          <YAxis
            scale={scale}
            // Always start at 0 for linear scale. For log scale, 0 is invalid, so we clamp to 1.
            domain={scale === 'log' ? [1, 'auto'] : [0, 'auto']}
            tickFormatter={formatAxisTick}
            className={`${textColors.tertiary} opacity-80`}
            stroke="currentColor"
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
            }}
            iconType="line"
          />
          
          {/* Users Line */}
          {showUsers && (
            <Line
              type="monotone"
              dataKey="users"
              name="Users"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}
          
          {/* Containers Line */}
          {showContainers && (
            <Line
              type="monotone"
              dataKey="containers"
              name="Containers"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeSeriesChart;

